import { PictureOutlined } from "@ant-design/icons";
import { DatePicker, Form, Input, Upload, Radio, TimePicker, Select } from "antd";
import { useTranslation } from "react-i18next";
import RichTextEditor from "components/shared-components/RichTextEditor";
import { isHtmlEmpty } from "utils/htmlContent";

const EventForm = ({ form, onSubmit }) => {
  const { t } = useTranslation();

  const normFile = (e) => (Array.isArray(e) ? e : e?.fileList);

  // Un éditeur riche "vide" vaut "" ou "<p><br></p>" : `required` ne suffit pas
  const richTextRequired = (message) => ({
    required: true, // pour l'astérisque du label
    validator: (_, value) =>
      isHtmlEmpty(value) ? Promise.reject(new Error(message)) : Promise.resolve(),
  });

  // Watch the postType field value directly from form
  const postType = Form.useWatch("postType", form) || "article";
  const eventFormat = Form.useWatch("format", form) || "online";
  const publishMode = Form.useWatch("publishMode", form) || "immediate";

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onSubmit}
      initialValues={{
        postType: "article",
        format: "online",
        publishMode: "immediate",
      }}
    >
      {/* TYPE SELECT */}
      <Form.Item
        name="postType"
        label="Type"
        rules={[{ required: true, message: "Type obligatoire" }]}
      >
        <Select
          placeholder="Choisir le type"
          options={[
            { value: "article", label: "Article" },
            { value: "event", label: "Événement" },
            { value: "testimonial", label: "Témoignage" },
            { value: "video", label: "Vidéo" },
          ]}
        />
      </Form.Item>

      {/* IMAGE */}
      <Form.Item
        name="image"
        valuePropName="fileList"
        getValueFromEvent={normFile}
      >
        <Upload listType="picture-card" maxCount={1} beforeUpload={() => false}>
          <div style={{ textAlign: "center" }}>
            <PictureOutlined style={{ fontSize: 24 }} />
            <div>Uploader</div>
          </div>
        </Upload>
      </Form.Item>

      {/* COMMON FIELDS */}
      <Form.Item
        name="title"
        label="Titre"
        rules={[{ required: true, message: "Titre obligatoire" }]}
      >
        <Input />
      </Form.Item>

      {/* DYNAMIC FIELDS */}
      {postType === "article" && (
        <>
          <Form.Item
            name="excerpt"
            label="Extrait"
            rules={[{ required: true, message: "Extrait obligatoire" }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item
            name="content"
            label="Contenu"
            rules={[richTextRequired("Contenu obligatoire")]}
            validateTrigger={["onChange", "onSubmit"]}
          >
            <RichTextEditor
              height={260}
              placeholder="Rédigez ou collez votre contenu (la mise en forme est conservée)…"
            />
          </Form.Item>
        </>
      )}

      {postType === "testimonial" && (
        <>
          <Form.Item
            name="studentName"
            label="Nom de l'étudiant"
            rules={[{ required: true, message: "Nom obligatoire" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="testimonial"
            label="Témoignage"
            rules={[richTextRequired("Témoignage obligatoire")]}
            validateTrigger={["onChange", "onSubmit"]}
          >
            <RichTextEditor height={180} />
          </Form.Item>

          <Form.Item name="videoUrl" label="Vidéo (optionnel)">
            <Input placeholder="https://youtube.com/..." />
          </Form.Item>
        </>
      )}

      {postType === "video" && (
        <Form.Item
          name="videoUrl"
          label="URL Vidéo"
          rules={[{ required: true, message: "Vidéo obligatoire" }]}
        >
          <Input placeholder="https://youtube.com/..." />
        </Form.Item>
      )}

      {postType === "event" && (
        <>
          <Form.Item
            name="description"
            label="Description"
            rules={[richTextRequired("Description obligatoire")]}
            validateTrigger={["onChange", "onSubmit"]}
          >
            <RichTextEditor height={200} />
          </Form.Item>

          <Form.Item name="format" label="Type d'événement">
            <Radio.Group
              options={[
                { label: "En ligne", value: "online" },
                { label: "Présentiel", value: "onsite" },
              ]}
            />
          </Form.Item>

          {eventFormat === "onsite" && (
            <Form.Item
              name="location"
              label="Lieu"
              rules={[{ required: true, message: "Lieu obligatoire" }]}
            >
              <Input />
            </Form.Item>
          )}

          <Form.Item
            name="eventDate"
            label="Date"
            rules={[{ required: true, message: "Date obligatoire" }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="eventTime"
            label="Heure"
            rules={[{ required: true, message: "Heure obligatoire" }]}
          >
            <TimePicker format="HH:mm" style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="registrationLink"
            label="Lien d'inscription"
            rules={[{ required: true, message: "Lien obligatoire" }]}
          >
            <Input />
          </Form.Item>
        </>
      )}

      <Form.Item
        name="publishMode"
        label={t("events.crud.form.publishMode", "Mode de publication")}
        rules={[
          {
            required: true,
            message: t(
              "events.crud.form.error.publishMode.required",
              "Veuillez sélectionner un mode de publication !"
            ),
          },
        ]}
      >
        <Radio.Group
          options={[
            {
              label: t(
                "events.crud.form.publishMode.immediate",
                "Publication immédiate"
              ),
              value: "immediate",
            },
            {
              label: t(
                "events.crud.form.publishMode.scheduled",
                "Publication programmée"
              ),
              value: "scheduled",
            },
          ]}
        />
      </Form.Item>

      {publishMode === "scheduled" && (
        <>
          <Form.Item
            name="publishDate"
            label={t("events.crud.form.publishDate", "Date de publication")}
            rules={[
              {
                required: true,
                message: t(
                  "events.crud.form.error.publishDate.required",
                  "Veuillez sélectionner la date de publication !"
                ),
              },
            ]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="publishTime"
            label={t("events.crud.form.publishTime", "Heure de publication")}
            rules={[
              {
                required: true,
                message: t(
                  "events.crud.form.error.publishTime.required",
                  "Veuillez sélectionner l'heure de publication !"
                ),
              },
            ]}
          >
            <TimePicker format="HH:mm" style={{ width: "100%" }} />
          </Form.Item>
        </>
      )}
    </Form>
  );
};

export default EventForm;
